import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Activity, Battery, Cpu, Droplets, Gauge, MapPin, Radio, RefreshCw, Thermometer, Zap,
} from 'lucide-react'
import {
  assetCockpitService,
  capabilityLabel,
  type AssetCockpitResponse,
  type CockpitDevice,
} from '../services/assetCockpitService'

export interface AssetCockpitWindowProps {
  assetId: string
  vehicleLabel?: string | null
  showMessage?: (msg: string, type: 'success' | 'error' | 'warning' | 'info') => void
  onOpenMap?: () => void
  onOpenCapability?: (windowId: string, imei: string, deviceName?: string | null) => void
}

function fmt(v: unknown, digits = 2): string {
  if (v == null || v === '') return '—'
  if (typeof v === 'number' && Number.isFinite(v)) return v.toFixed(digits)
  if (typeof v === 'boolean') return v ? 'ON' : 'OFF'
  return String(v)
}

function Tile({
  icon, label, value, unit, tone = 'slate',
}: {
  icon: React.ReactNode
  label: string
  value: string
  unit?: string | null
  tone?: 'slate' | 'green' | 'amber' | 'red' | 'blue'
}) {
  const tones = {
    slate: 'bg-slate-50 border-slate-200 text-slate-700',
    green: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    red: 'bg-red-50 border-red-200 text-red-800',
    blue: 'bg-blue-50 border-blue-200 text-blue-800',
  }
  return (
    <div className={`rounded-xl border p-3 ${tones[tone]}`}>
      <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide opacity-70">
        {icon}{label}
      </div>
      <div className="mt-1 text-lg font-semibold tabular-nums">
        {value}{unit && value !== '—' ? <span className="ml-1 text-xs font-normal opacity-70">{unit}</span> : null}
      </div>
    </div>
  )
}

const DeviceCard: React.FC<{
  device: CockpitDevice
  onOpenMap?: () => void
  onOpenCapability?: (windowId: string, imei: string, deviceName?: string | null) => void
}> = ({ device, onOpenMap, onOpenCapability }) => {
  const { t } = useTranslation()
  const p = device.peripheral
  const tel = device.telemetry
  const brand = String(p.brand || '').toUpperCase()
  const isTeltonika = brand === 'TELTONIKA'
  const ignTone = tel?.ignition == null ? 'slate' : tel.ignition ? 'green' : 'red'

  return (
    <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <header className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2 justify-between bg-slate-50/80">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-slate-900 truncate">{p.deviceName || p.lockCode}</h3>
            <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-violet-100 text-violet-700">{brand || '—'}</span>
            <span className="text-[10px] font-semibold uppercase px-2 py-0.5 rounded bg-slate-200 text-slate-700">
              {capabilityLabel(p.capability)}
            </span>
            {tel?.online != null && (
              <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded ${
                tel.online ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'
              }`}>
                {tel.online ? 'Online' : 'Offline'}
              </span>
            )}
          </div>
          <p className="text-[11px] text-slate-500 font-mono mt-0.5 truncate">
            {p.lockCode}{p.imei ? ` · IMEI ${p.imei}` : ''}{tel?.model ? ` · ${tel.model}` : ''}
          </p>
        </div>
        <div className="flex gap-2">
          {onOpenMap && (tel?.latitude != null || p.lat != null) && (
            <button type="button" onClick={onOpenMap}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100">
              <MapPin size={12} />{t('cockpit_show_map', { defaultValue: 'Map' })}
            </button>
          )}
          {onOpenCapability && device.deepLinkWindow && device.deepLinkWindow !== 'map'
            && device.deepLinkWindow !== 'assetCockpit' && p.imei && brand === 'JIMI' && (
            <button type="button"
              onClick={() => onOpenCapability(device.deepLinkWindow!, p.imei!, p.deviceName)}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-lg border border-cyan-200 bg-cyan-50 text-cyan-700 hover:bg-cyan-100">
              <Cpu size={12} />{t('cockpit_open_detail', { defaultValue: 'Detail window' })}
            </button>
          )}
        </div>
      </header>

      <div className="p-4 space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
          <Tile icon={<MapPin size={12} />} label={t('cockpit_gps', { defaultValue: 'GPS' })}
            value={tel?.latitude != null && tel?.longitude != null
              ? `${Number(tel.latitude).toFixed(5)}, ${Number(tel.longitude).toFixed(5)}`
              : p.lat != null && p.lng != null
                ? `${Number(p.lat).toFixed(5)}, ${Number(p.lng).toFixed(5)}`
                : '—'}
            tone="blue" />
          <Tile icon={<Gauge size={12} />} label={t('cockpit_speed', { defaultValue: 'Speed' })}
            value={fmt(tel?.speedKmh, 0)} unit="km/h" />
          <Tile icon={<Zap size={12} />} label={t('cockpit_ignition', { defaultValue: 'Ignition' })}
            value={tel?.ignition == null ? '—' : (tel.ignition ? 'ON' : 'OFF')} tone={ignTone as any} />
          <Tile icon={<Activity size={12} />} label={t('cockpit_movement', { defaultValue: 'Movement' })}
            value={tel?.movement == null ? '—' : (tel.movement ? 'YES' : 'NO')} />
          <Tile icon={<Battery size={12} />} label={t('cockpit_battery', { defaultValue: 'Battery' })}
            value={fmt(tel?.batteryLevel ?? p.batteryLevel, 0)} unit="%" />
          <Tile icon={<Zap size={12} />} label={t('cockpit_ext_voltage', { defaultValue: 'Ext. voltage' })}
            value={fmt(tel?.externalVoltageV, 2)} unit="V" tone={isTeltonika ? 'amber' : 'slate'} />
          <Tile icon={<Radio size={12} />} label={t('cockpit_last_seen', { defaultValue: 'Last seen' })}
            value={tel?.lastSeen || p.lastSeen
              ? new Date(String(tel?.lastSeen || p.lastSeen)).toLocaleString()
              : '—'} />
          <Tile icon={<Gauge size={12} />} label={t('cockpit_odometer', { defaultValue: 'Odometer' })}
            value={tel?.odometerM != null ? fmt(Number(tel.odometerM) / 1000, 1) : '—'} unit="km" />
        </div>

        {!!tel?.captors?.length && (
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
              <Droplets size={12} />{t('cockpit_captors', { defaultValue: 'Captors & IO' })}
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {tel.captors.map((c) => (
                <Tile key={c.id} icon={<Cpu size={12} />} label={c.label}
                  value={typeof c.value === 'number' ? fmt(c.value, 1) : fmt(c.value)}
                  unit={c.unit} />
              ))}
            </div>
          </div>
        )}

        {!!device.ble?.length && (
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
              <Thermometer size={12} />{t('cockpit_ble', { defaultValue: 'BLE sensors (EYE / tags)' })}
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {device.ble.map((b, i) => (
                <div key={b.bleId || i} className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm">
                  <div className="font-semibold text-slate-800">{b.model || 'BLE'} · {b.bleId || '—'}</div>
                  <div className="mt-1 grid grid-cols-3 gap-2 text-xs text-slate-600">
                    <span>Temp {fmt(b.temperature, 1)} °C</span>
                    <span>Hum {fmt(b.humidity, 0)} %</span>
                    <span>Bat {fmt(b.battery, 0)} %</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tel?.note && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">{tel.note}</p>
        )}
        {tel?.gatewayError && (
          <p className="text-[11px] text-slate-500">
            {t('cockpit_gateway_partial', { defaultValue: 'Live gateway enrich unavailable' })}: {tel.gatewayError}
          </p>
        )}
      </div>
    </section>
  )
}

const AssetCockpitWindow: React.FC<AssetCockpitWindowProps> = ({
  assetId, vehicleLabel, showMessage, onOpenMap, onOpenCapability,
}) => {
  const { t } = useTranslation()
  const [data, setData] = useState<AssetCockpitResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!assetId) return
    setLoading(true)
    setError(null)
    try {
      const res = await assetCockpitService.getCockpit(assetId)
      setData(res)
    } catch (e: any) {
      const msg = e?.message || 'Failed to load cockpit'
      setError(msg)
      showMessage?.(msg, 'error')
    } finally {
      setLoading(false)
    }
  }, [assetId, showMessage])

  useEffect(() => { load() }, [load])

  const title = data?.vehicle?.name || data?.vehicle?.code || vehicleLabel || assetId

  return (
    <div className="h-full overflow-auto bg-[#F4F6F9]">
      <div className="sticky top-0 z-10 px-4 py-3 border-b border-slate-200 bg-white/95 backdrop-blur flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-base font-bold text-slate-900 truncate">
            {t('cockpit_title', { defaultValue: 'Vehicle cockpit' })} — {title}
          </h2>
          <p className="text-[11px] text-slate-500 truncate">
            {[data?.vehicle?.plate, data?.vehicle?.customerName, data?.vehicle?.brand, data?.vehicle?.model]
              .filter(Boolean).join(' · ') || t('cockpit_subtitle', { defaultValue: 'All balises and captors attached to this asset' })}
          </p>
        </div>
        <button type="button" onClick={load} disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          {t('refresh', { defaultValue: 'Refresh' })}
        </button>
      </div>

      <div className="p-4 space-y-4">
        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>
        )}
        {loading && !data && (
          <div className="text-sm text-slate-500">{t('loading', { defaultValue: 'Loading…' })}</div>
        )}
        {data && data.deviceCount === 0 && (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-slate-500 text-sm">
            {t('cockpit_empty', { defaultValue: 'No balise or captor is assigned to this vehicle yet. Assign one in Balise Management.' })}
          </div>
        )}
        {data?.devices.map((d) => (
          <DeviceCard
            key={d.peripheral.lockCode || d.peripheral.id}
            device={d}
            onOpenMap={onOpenMap}
            onOpenCapability={onOpenCapability}
          />
        ))}
        {data?.generatedAt && (
          <p className="text-[10px] text-slate-400 text-right">
            {t('cockpit_generated', { defaultValue: 'Generated' })} {new Date(data.generatedAt).toLocaleString()}
          </p>
        )}
      </div>
    </div>
  )
}

export default AssetCockpitWindow
